import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Avatar, Badge } from '../../components/ui';
import { motion } from 'framer-motion';
import { doc, updateDoc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/core/firebase';
import { useAuth } from '@/core/AuthContext';
import { TrainerProfilePhotoUpload } from '../../components/trainer/TrainerProfilePhotoUpload';
import type { Trainer } from '../../types';

export function ProfileSection() {
  const { userData, user } = useAuth();
  const isTrainer = userData?.role === 'trainer';
  const isClient = userData?.role === 'client';

  const [displayName, setDisplayName] = useState(userData?.displayName || '');
  const [preferredUnit, setPreferredUnit] = useState<'kg' | 'lb'>('lb');
  const [saving, setSaving] = useState(false);

  const [trainerProfile, setTrainerProfile] = useState<Trainer | null>(null);
  const [trainerProfileLoading, setTrainerProfileLoading] = useState(false);
  const [trainerPhotoURL, setTrainerPhotoURL] = useState('');
  const [trainerBio, setTrainerBio] = useState('');
  const [trainerCertifications, setTrainerCertifications] = useState('');

  useEffect(() => {
    if (!userData) return;
    setDisplayName(userData.displayName || '');
    setPreferredUnit((userData as any).preferredUnit || 'lb');
  }, [userData]);

  useEffect(() => {
    const trainerId = userData?.trainerProfileId || userData?.trainerId || user?.uid;
    if (!isTrainer || !trainerId) {
      setTrainerProfile(null);
      setTrainerPhotoURL('');
      setTrainerBio('');
      setTrainerCertifications('');
      return;
    }

    const fetchTrainerProfile = async () => {
      setTrainerProfileLoading(true);
      try {
        const trainerDoc = await getDoc(doc(db, 'trainers', trainerId));
        if (trainerDoc.exists()) {
          const profile = { id: trainerDoc.id, ...trainerDoc.data() } as Trainer;
          setTrainerProfile(profile);
          setTrainerPhotoURL(profile.photoURL || '');
          setTrainerBio(profile.bio || '');
          setTrainerCertifications(profile.certifications || '');
        } else {
          setTrainerProfile(null);
        }
      } catch (error) {
        console.error('Failed to load trainer profile:', error);
      } finally {
        setTrainerProfileLoading(false);
      }
    };

    void fetchTrainerProfile();
  }, [isTrainer, userData?.trainerProfileId, userData?.trainerId, user?.uid]);

  const handleSaveProfile = async () => {
    if (!userData) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', userData.id), {
        displayName,
        preferredUnit,
      });

      if (isTrainer) {
        const trainerId = userData.trainerProfileId || userData.trainerId || user?.uid;
        if (trainerId) {
          const nextTrainerProfile = {
            name: displayName.trim(),
            photoURL: trainerPhotoURL.trim(),
            bio: trainerBio.trim().slice(0, 500),
            certifications: trainerCertifications.trim(),
            ownerUserId: userData.id,
            updatedAt: Timestamp.now(),
          };

          await setDoc(doc(db, 'trainers', trainerId), nextTrainerProfile, { merge: true });
          setTrainerProfile((prev) => ({
            id: trainerId,
            secretLoginToken: prev?.secretLoginToken || '',
            createdAt: prev?.createdAt || Timestamp.now(),
            ...prev,
            ...nextTrainerProfile,
          }));
        }
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={isClient ? 'order-[-30]' : undefined}
      >
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Profile</CardTitle>
                <CardDescription>Your personal information</CardDescription>
              </div>
              <Badge variant="info">{userData?.role}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar
                  src={isTrainer ? trainerPhotoURL || undefined : undefined}
                  size="xl"
                  fallback={displayName}
                  alt={displayName}
                />
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">Avatar</p>
                  <p className="text-xs text-gray-500 dark:text-slate-500">
                    {isTrainer ? 'Clients will see this on your profile.' : 'Upload coming soon'}
                  </p>
                </div>
              </div>

              {isTrainer && (
                <TrainerProfilePhotoUpload
                  trainerId={userData?.trainerProfileId || userData?.trainerId || user?.uid || userData.id}
                  value={trainerPhotoURL}
                  onChange={setTrainerPhotoURL}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="form-field max-w-md"
                  placeholder="Your name"
                  data-testid="name-input"
                />
              </div>

              {isTrainer && (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                        Bio
                      </label>
                      <span className="text-xs text-gray-500 dark:text-slate-500">
                        {trainerBio.length}/500
                      </span>
                    </div>
                    <textarea
                      value={trainerBio}
                      onChange={(e) => setTrainerBio(e.target.value.slice(0, 500))}
                      rows={5}
                      className="w-full max-w-2xl px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition resize-none"
                      placeholder="Tell clients about your approach, style, and what you help people achieve."
                      data-testid="trainer-settings-bio-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Certifications
                    </label>
                    <input
                      type="text"
                      value={trainerCertifications}
                      onChange={(e) => setTrainerCertifications(e.target.value)}
                      className="form-field max-w-2xl"
                      placeholder="NASM-CPT, CrossFit L2, Precision Nutrition"
                      data-testid="trainer-settings-certifications-input"
                    />
                    <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                      These are shown separately from your bio and are not verified by the platform.
                    </p>
                  </div>
                </>
              )}

              {isTrainer && trainerProfileLoading && (
                <p className="text-sm text-gray-500 dark:text-slate-500">Loading trainer profile...</p>
              )}

              {isTrainer && trainerProfile && (
                <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Trainer profile preview
                    </p>
                    <Badge variant="primary">Visible to clients</Badge>
                  </div>
                  {trainerBio.trim() && (
                    <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                      {trainerBio.trim()}
                    </p>
                  )}
                  {trainerCertifications.trim() && (
                    <div className="flex flex-wrap gap-2">
                      {trainerCertifications
                        .split(',')
                        .map((item) => item.trim())
                        .filter(Boolean)
                        .map((item) => (
                          <Badge key={item} variant="info">
                            {item}
                          </Badge>
                        ))}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Preferred Weight Unit
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPreferredUnit('lb')}
                    data-testid="preferred-unit-lb-button"
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      preferredUnit === 'lb'
                        ? 'bg-primary-50 dark:bg-primary-500/10 border-primary-500 text-primary-700 dark:text-primary-400'
                        : 'border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5'
                    }`}
                  >
                    Pounds (lb)
                  </button>
                  <button
                    onClick={() => setPreferredUnit('kg')}
                    data-testid="preferred-unit-kg-button"
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      preferredUnit === 'kg'
                        ? 'bg-primary-50 dark:bg-primary-500/10 border-primary-500 text-primary-700 dark:text-primary-400'
                        : 'border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5'
                    }`}
                  >
                    Kilograms (kg)
                  </button>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleSaveProfile}
                  isLoading={saving}
                  disabled={saving}
                  data-testid="save-profile-button"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
}
